import { createAsyncThunk } from '@reduxjs/toolkit';
import { HttpClient } from '@wix/http-client';

import { logger } from '../../util/logger';
import { renderModel } from '../../util/renderModel';
import { httpClient } from '../httpClient';
import { EDITOR_TYPE } from '../messageCreators';
import { TRootState } from '../reducers';
import { ISiteItem } from '../reducers/sites';

const { clientTopology } = renderModel.get();

interface ILastInteractedSiteResponse {
  site?: {
    metaSiteId: string;
    name: string;
    viewerUrl: string;
    published: boolean;
    dateUpdated: string;
    ownerId: string;
    premiumAssigned: boolean;
    editorType?: string;
  };
}

const sortSitesByDate = (a: ISiteItem, b: ISiteItem): number => {
  return Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
};

const convertEditorType = (type: string | undefined): EDITOR_TYPE => {
  if (type && Object.values(EDITOR_TYPE).includes(type as EDITOR_TYPE)) {
    return type as EDITOR_TYPE;
  }
  return EDITOR_TYPE.UNKNOWN_TYPE;
};

const mapSiteEntry = (userId: string) => {
  enum USER_ROLE {
    OWNER = 'owner',
    CONTRIBUTOR = 'contributor',
  }

  return (site: any): ISiteItem => ({
    id: site.metaSiteId,
    name: site.name,
    url: site.viewerUrl,
    published: site.published,
    updatedAt: site.dateUpdated,
    thumbnail: `https://www.wix.com/site-thumbnail/${site.metaSiteId}?preset=site-details`,
    userRole: site.ownerId === userId ? USER_ROLE.OWNER : USER_ROLE.CONTRIBUTOR,
    premium: site.premiumAssigned,
    editorType: convertEditorType(site.editor?.editorType),
  });
};

const fetchSitesApi = (userId: string): Promise<ISiteItem[]> => {
  const body = {
    responseData: {
      paths: [
        'metaSiteId',
        'name',
        'premiumFeatures',
        'published',
        'dateUpdated',
        'viewerUrl',
        'ownerId',
        'editor',
        'premiumAssigned',
      ],
    },
  };
  const metaSiteHttpClient = new HttpClient();

  return metaSiteHttpClient
    .post('/meta-site-search-web/v2/search', body)
    .then(({ data }) => {
      if (Array.isArray(data.entries)) {
        const mapSites = mapSiteEntry(userId);
        return data.entries.map(mapSites).sort(sortSitesByDate);
      }
      throw new Error('Invalid response format');
    })
    .catch((error) => {
      logger.error({
        error,
        message: 'Failed to fetch user sites',
        callParams: { body, userId },
      });
      return Promise.reject(error);
    });
};

const fetchLastInteractedSite = (userId: string): Promise<ISiteItem | undefined> => {
  return httpClient
    .getInstance()
    .get(clientTopology.lastInteractedSiteUrl)
    .then(({ data }) => {
      const response = data as ILastInteractedSiteResponse;
      if (!response.site) {
        return undefined;
      }

      const site = response.site;
      return {
        id: site.metaSiteId,
        name: site.name,
        url: site.viewerUrl,
        published: site.published,
        updatedAt: site.dateUpdated,
        thumbnail: `https://www.wix.com/site-thumbnail/${site.metaSiteId}?preset=site-details`,
        userRole: site.ownerId === userId ? 'owner' : 'contributor',
        premium: site.premiumAssigned,
        editorType: convertEditorType(site.editorType),
      };
    })
    .catch((error) => {
      logger.error({
        error,
        message: 'Failed to fetch last interacted site',
      });
      return undefined;
    });
};

interface FetchSitesResult {
  sites: ISiteItem[];
  selectedSite: ISiteItem | null;
}

export const fetchSites = createAsyncThunk<FetchSitesResult, string, { state: TRootState }>(
  'sites/fetchSites',
  async (userId: string) => {
    const [sites, lastInteractedSite] = await Promise.all([
      fetchSitesApi(userId),
      fetchLastInteractedSite(userId),
    ]);

    let selectedSite: ISiteItem | null = null;

    if (lastInteractedSite) {
      // First try to find the site in the already-fetched paginated list
      selectedSite = sites.find((site) => site.id === lastInteractedSite.id) || null;

      // If not found in the paginated list, use the server-fetched site data
      if (!selectedSite) {
        selectedSite = lastInteractedSite;
      }
    }

    // Fallback to first site (most recently updated) if no last interacted site
    if (!selectedSite && sites.length > 0) {
      selectedSite = sites[0];
    }

    return { sites, selectedSite };
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      // Don't fetch if already fetched or currently loading
      return !state.sites?.hasFetched && !state.sites?.isLoading;
    },
  },
);
